import React, { useState } from "react";
import { useDonorQueryStats } from "../../../hooks/useDonorQuery";

const DAY_OPTIONS = [7, 15, 30];

const AdminDonorQuery = () => {
  const [days, setDays] = useState(30);
  const { data, isLoading, error } = useDonorQueryStats(days);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Donor Query</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="skeleton h-28 rounded-lg"></div>
          ))}
        </div>
        <div className="skeleton h-64 rounded-lg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Failed to load donor query stats: {error.message}</span>
      </div>
    );
  }

  const { overview, topCombinations, byGroup, byDistrict, recent } = data?.data || {};

  const maxGroupCount = Math.max(...(byGroup?.map((g) => g.count) || [1]));
  const maxDistrictCount = Math.max(...(byDistrict?.map((d) => d.count) || [1]));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Donor Query</h1>
        <div className="btn-group">
          {DAY_OPTIONS.map((d) => (
            <button
              key={d}
              className={`btn btn-sm ${days === d ? "btn-active" : ""}`}
              onClick={() => setDays(d)}
            >
              {d} Days
            </button>
          ))}
        </div>
      </div>

      {/* Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="stat shadow bg-blue-600 text-white rounded-lg">
          <div className="stat-figure text-3xl">
            <i className="fa-solid fa-magnifying-glass"></i>
          </div>
          <div className="stat-title text-white">Total Searches</div>
          <div className="stat-value">{overview?.totalQueries?.toLocaleString() || 0}</div>
        </div>
        <div className="stat shadow bg-purple-600 text-white rounded-lg">
          <div className="stat-figure text-3xl">
            <i className="fa-solid fa-layer-group"></i>
          </div>
          <div className="stat-title text-white">Unique Combinations</div>
          <div className="stat-value">{overview?.uniqueCombinations?.toLocaleString() || 0}</div>
        </div>
      </div>

      {/* Top Combinations Table */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">Top Searched Combinations (Last {days} Days)</h2>
          <div className="overflow-x-auto mt-2">
            <table className="table table-zebra table-sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Blood Group</th>
                  <th>District</th>
                  <th className="text-right">Searches</th>
                </tr>
              </thead>
              <tbody>
                {topCombinations?.map((q, i) => (
                  <tr key={i}>
                    <td className="text-xs">{i + 1}</td>
                    <td>
                      <span className="badge badge-sm badge-error">{q.group}</span>
                    </td>
                    <td className="text-xs">{q.district}</td>
                    <td className="text-right text-xs font-medium">{q.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!topCombinations || topCombinations.length === 0) && (
              <div className="text-center text-gray-500 py-4">No data yet</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Blood Group */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Searches by Blood Group</h2>
            <div className="space-y-2 mt-2">
              {byGroup?.map((g) => (
                <div key={g.group} className="flex items-center gap-2">
                  <span className="badge badge-sm badge-error w-10">{g.group}</span>
                  <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-red-500 h-3 rounded-full"
                        style={{ width: `${(g.count / maxGroupCount) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-xs font-medium w-12 text-right">{g.count.toLocaleString()}</span>
                </div>
              ))}
              {(!byGroup || byGroup.length === 0) && (
                <div className="text-center text-gray-500 py-4">No data yet</div>
              )}
            </div>
          </div>
        </div>

        {/* By District */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Top Districts</h2>
            <div className="space-y-2 mt-2">
              {byDistrict?.map((d) => (
                <div key={d.district} className="flex items-center gap-2">
                  <span className="text-xs w-24 truncate">{d.district}</span>
                  <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-blue-500 h-3 rounded-full"
                        style={{ width: `${(d.count / maxDistrictCount) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-xs font-medium w-12 text-right">{d.count.toLocaleString()}</span>
                </div>
              ))}
              {(!byDistrict || byDistrict.length === 0) && (
                <div className="text-center text-gray-500 py-4">No data yet</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Queries */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">Recent Queries</h2>
          <div className="overflow-x-auto mt-2">
            <table className="table table-zebra table-sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Group</th>
                  <th>District</th>
                  <th className="text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {recent?.map((q, i) => (
                  <tr key={i}>
                    <td className="text-xs whitespace-nowrap">
                      {new Date(q.createdAt).toLocaleString()}
                    </td>
                    <td>
                      <span className="badge badge-sm badge-error">{q.group}</span>
                    </td>
                    <td className="text-xs">{q.district}</td>
                    <td className="text-right text-xs font-medium">{q.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!recent || recent.length === 0) && (
              <div className="text-center text-gray-500 py-4">No data yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDonorQuery;
